import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Button } from '@sunwebgroup/uds-ui/button'
import { CheckboxField } from '@sunwebgroup/uds-ui/fields/checkbox-field'
import { DateField } from '@sunwebgroup/uds-ui/fields/date-field'
import { FormField } from '@sunwebgroup/uds-ui/fields/form-field'
import { RadioButtonField } from '@sunwebgroup/uds-ui/fields/radio-button-field'
import { RadioButtonGroup } from '@sunwebgroup/uds-ui/radio-button'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Typography } from '@sunwebgroup/uds-ui/typography'

import { format, sub } from 'date-fns'
import * as React from 'react'
import { Controller, FormProvider, useController, useForm, useFormContext } from 'react-hook-form'
import { useIntl } from '@/intl'

import { validateAgeBetweenDates } from '@/scopes/checkout/components/helpers/Validations'
import {
  CarListName,
  CarSheetContent,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/SheetViews'
import { carCouldHaveHigherPrice, editingCar, useCarRentalFilteringServiceStore } from '@/scopes/checkout/components/services/CarRentalFilteringService/store'
import { addCar, editCar } from '@/scopes/checkout/domain/actions/services/carRentalFiltering'
import { createCarEvent } from '@/scopes/checkout/domain/analytics'
import { useService } from '@/scopes/checkout/domain/hooks'
import { type CheckoutCarRentalServiceOption, ServiceType } from '@/scopes/checkout/domain/types'
import { getErrorMessage } from '@/scopes/common/components/FormElements/getErrorMessage'
import { useStatus } from '@/scopes/common/components/FormElements/useStatus'
import { isValidDate, parseDateForLocalFormatting, parseDay } from '@/scopes/common/domain/utils/dates'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import styles from './Driver.module.css'

interface DriverFormValues {
  driverId: string
  driverBirthdate: string | Date
  hasCreditCard: boolean
}

export function DriverForm({ showDriverForm, setShowDriverForm, ...option }: CheckoutCarRentalServiceOption & { showDriverForm: boolean, setShowDriverForm: (value: boolean) => void }) {
  const { service, setServiceValue } = useService(ServiceType.CarRentalFiltering)
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { endEditingCar, setCarToBeSelected,
  } = useCarRentalFilteringServiceStore()
  const { availableDrivers, participants, configurations, carLists, hasUnderageAdults } = service || {}
  const { driverId, label, value: carValue, id: carId } = option

  const currentEditingCar = useCarRentalFilteringServiceStore(editingCar(carId))
  const {
    bookedEquipmentsWithQuantity,
  } = currentEditingCar || {}

  const defaultDriverBirthdate = sub(new Date(), { years: configurations?.rentalConditionsMinAgeDriver })
  const driver = driverId ? participants?.[driverId] : null
  const formRef = React.useRef<HTMLFormElement>(null)

  const form = useForm<DriverFormValues>({
    mode: 'onBlur',
    defaultValues: {
      driverId: driverId ?? '',
      driverBirthdate:
         driver?.birthdate ? parseDateForLocalFormatting(driver.birthdate) : new Date(defaultDriverBirthdate),
      hasCreditCard: Boolean(driverId && driver?.birthdate ? (driver?.id === driverId) : false),
    },
  })
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = form

  const handleDriverChange = (value: string) => {
    setValue('driverId', value)
    const selectedDriver = participants?.[value]

    setValue(
      'driverBirthdate',
      selectedDriver?.birthdate
        ? parseDateForLocalFormatting(selectedDriver.birthdate)
        : new Date(defaultDriverBirthdate),
    )

    setValue('hasCreditCard', selectedDriver?.id ? (driver?.id === selectedDriver?.id) : false)
  }

  const onSubmit = React.useCallback((result: DriverFormValues) => {
    if (!service)
      return
    setCarToBeSelected('')
    endEditingCar(carId)
    setShowDriverForm(false)
    const updatedBirthdate = result.driverBirthdate instanceof Date
      ? result.driverBirthdate
      : parseDateForLocalFormatting(result.driverBirthdate)
    if (driver) {
      setServiceValue(editCar({
        value: carValue,
        id: carId,
        participantId: result.driverId ?? '',
        driverBirthDate: updatedBirthdate,
        bookedEquipmentIds: bookedEquipmentsWithQuantity?.map(({ id }) => id) ?? [],
        bookedEquipmentsWithQuantity: bookedEquipmentsWithQuantity ?? [],
      })(service))
      sendEvent(
        createCarEvent({
          result: 'Car edited',
          type: carValue,
          description: 'editCarDriver',
          element: 'button',
          validated: true,
        }),
      )
    }
    else {
      setServiceValue(addCar({
        value: carValue,
        id: carId,
        participantId: result.driverId ?? '',
        driverBirthDate: updatedBirthdate,
        bookedEquipmentIds: [],
      })(service))
      sendEvent(
        createCarEvent({
          result: 'Car added',
          type: carValue,
          description: 'selectCar',
          element: 'button',
          validated: true,
        }),
      )
    }
  }, [driver, setShowDriverForm, service, setServiceValue, endEditingCar, option, sendEvent, setCarToBeSelected]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!service)
    return null
  if (!option)
    return null
  if (!participants)
    return null

  return (
    <>
      <Sheet.Content>
        <FormProvider {...form}>
          <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
            <CarSheetContent>
              <CarListName variant="display-h-regular">
                {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_car_model' }, { carModel: label })}
                {' '}
              </CarListName>
              <Typography variant="body-m-regular-snug">{intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_intro_text' })}</Typography>
              <FormField
                label={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_label' })}
                helpText={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_hint' })}
              >
                <div className={styles.formFieldContent}>
                  <Controller
                    name="driverId"
                    control={control}
                    rules={{
                      required: intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_required_message' }),
                    }}

                    render={({ field }) => (
                      <RadioButtonGroup defaultValue={field.value} onValueChange={handleDriverChange}>
                        <div className={styles.radioButtonGroupContainer}>
                          {availableDrivers?.map((availableDriver) => {
                            const isDisabled = driverId === availableDriver ? false : Boolean(carLists?.find(car => car.driverId === availableDriver))

                            return (
                              <RadioButtonField key={availableDriver} variant="outlined" className={styles.driverName} disabled={isDisabled} value={availableDriver} label={participants[availableDriver]?.name} />
                            )
                          })}
                        </div>
                      </RadioButtonGroup>
                    )}
                  />
                  {errors.driverId && (
                    <Banner color="danger" hasIcon size="M">
                      <Banner.TextsWrapper>
                        <Banner.Text>{errors.driverId.message ?? ''}</Banner.Text>
                      </Banner.TextsWrapper>
                    </Banner>
                  )}

                </div>
              </FormField>
              {hasUnderageAdults && (
                <Banner color="neutral" hasIcon size="S">
                  <Banner.TextsWrapper>
                    <Banner.Text>{intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_underage_banner' }, { minAge: configurations?.minAgeDriver })}</Banner.Text>
                  </Banner.TextsWrapper>
                </Banner>
              )}
              <DriverBirthdateField {...option} />
              <FormField
                status={errors.hasCreditCard ? 'error' : undefined}
              >
                <div className={styles.formFieldContent}>
                  <Controller
                    name="hasCreditCard"
                    control={control}
                    rules={{
                      required: intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_credit_card_required_message' }),
                    }}
                    render={({ field }) => (
                      <CheckboxField
                        label={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_credit_card_label' })}
                        variant="outlined"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        onClick={field.onChange}
                        id={field.name}
                      />
                    )}
                  />
                  {errors.hasCreditCard && (
                    <Banner color="danger" hasIcon size="M">
                      <Banner.TextsWrapper>
                        <Banner.Text>{errors.hasCreditCard.message ?? ''}</Banner.Text>
                      </Banner.TextsWrapper>
                    </Banner>
                  )}
                </div>
              </FormField>
            </CarSheetContent>
          </form>
        </FormProvider>

      </Sheet.Content>
      <Sheet.Footer>
        <Button variant="primary" size="M" fullWidth onClick={handleSubmit(onSubmit)}>
          {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_save_button_text' })}
        </Button>
      </Sheet.Footer>
    </>
  )
}

function DriverBirthdateField({ ...option }: CheckoutCarRentalServiceOption) {
  const name = 'driverBirthdate'
  const dateFormat = useConfiguration('dateFormat')
  const { formState, control } = useFormContext<DriverFormValues>()
  const { service } = useService(ServiceType.CarRentalFiltering)
  const { configurations } = service || {}
  const { id: carId } = option

  const intl = useIntl()
  const driverMinBirthdate = sub(new Date(), { years: 99 })
  const driverMaxBirthdate = sub(new Date(), { years: configurations?.minAgeDriver })
  const driverMinAgeRentalConditions = sub(new Date(), { years: configurations?.rentalConditionsMinAgeDriver })
  const driverMaxAgeRentalConditions = sub(new Date(), { years: configurations?.rentalConditionsMaxAgeDriver })
  const [showWarningDriverAge, setShowWarningDriverAge] = React.useState(useCarRentalFilteringServiceStore(carCouldHaveHigherPrice(carId, driverMaxAgeRentalConditions, driverMinAgeRentalConditions)))

  const maxBirthdate = driverMaxBirthdate ? new Date(driverMaxBirthdate) : undefined
  const minBirthdate = driverMinBirthdate ? new Date(driverMinBirthdate) : undefined

  const { errors } = formState

  const validations = {
    validateAge: (value: any) => {
      const delegateRequiredValidation = !value
      if (delegateRequiredValidation)
        return true

      return validateAgeBetweenDates(value, minBirthdate, maxBirthdate, dateFormat)
    },
  }

  const {
    field: { value, onChange, ref, onBlur },
  } = useController({
    name,
    control,
    rules: {
      required: true,
      validate: validations,
    },
  })
  const [inputValue, setInputValue] = React.useState<string>(
    value instanceof Date ? format(value, dateFormat) : (value || ''),
  )

  React.useEffect(() => {
    // Sync the visible input whenever value is set externally (e.g. driver change)
    if (value instanceof Date) {
      setInputValue(format(value, dateFormat))
    }

    if (!value)
      return
    const isValidAge = validateAgeBetweenDates(value, minBirthdate, maxBirthdate, dateFormat)
    if (isValidAge) {
      const selected = new Date(value)
      const isLegalAge = selected >= driverMaxAgeRentalConditions && selected <= driverMinAgeRentalConditions
      setShowWarningDriverAge(!isLegalAge)
    }
    else {
      setShowWarningDriverAge(false)
    }
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  const errorMessage = getErrorMessage(name, errors, {
    requiredErrorMessage: intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_birthdate_required_message', defaultMessage: 'Date not valid' }),
    validateErrorMessage: intl.formatMessage(
      { id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_birthdate_no_legal_age_message' },
      { min: configurations?.minAgeDriver },
    ),
  })

  const status = useStatus({
    name,
    required: true,
    validations,
    shouldMarkValidStatus: (value: string) => Boolean(value),
  })

  return (
    <>
      <DateField
        ref={ref}
        name={name}
        dateFormat={dateFormat}
        value={inputValue}
        onChange={(formattedValue: string) => {
          if (!formattedValue) {
            onChange('')
            setInputValue('')
            return
          }

          setInputValue(formattedValue)

          if (formattedValue.length !== dateFormat.length) {
            onChange(formattedValue)
            return
          }

          const parsedDate = parseDay(formattedValue, dateFormat)
          onChange(isValidDate(parsedDate) ? parsedDate : formattedValue)
        }}
        onBlur={onBlur}
        feedbackText={errorMessage}
        status={status}
        label={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_birthdate' })}
        helpText={dateFormat.toUpperCase()}
      />
      {!errors?.driverBirthdate && showWarningDriverAge && (
        <Banner color="neutral" size="S">
          <Banner.TextsWrapper>
            <Banner.Text>
              {intl.formatMessage(
                { id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_birthdate_out_of_bounds_message' },
                {
                  min: configurations?.rentalConditionsMinAgeDriver,
                  max: configurations?.rentalConditionsMaxAgeDriver,
                },
              )}
            </Banner.Text>
          </Banner.TextsWrapper>
        </Banner>
      )}
    </>
  )
}
